import {MenuItem} from './MenuItem'
import {MenuInnerWithSub} from './MenuInnerWithSub'
import {useIntl} from 'react-intl'
import {Route, useNavigate} from 'react-router-dom'
import { MouseEvent } from 'react'
// Removed AppliedDailyDealListPage import as it's not used in this component directly
// import AppliedDailyDealListPage from "../../../../../app/pages/daily-deals/AppliedDailyDealListPage"; 
import { useSupplierInfo } from '../../../core/SupplierInfoProvider' // Import useSupplierInfo
import { usePermission } from '../../../../../app/hooks/usePermission' // Import usePermission hook
import {tenantFeatureEnabled} from '../../../../../app/tenant/tenantState'
import SharedData from '../../../../../app/modules/common/SharedData'
import {isShopUser} from '../../../../../app/modules/common/stockScope'
import {useUnseenStockRequests} from '../../../../../app/modules/add-to-stock/core/useUnseenStockRequests'
import {directionClasses} from '../../../../../app/modules/add-to-stock/core/direction'
import {useLang} from '../../../../i18n/Metronici18n'
// Uncomment the line below to use the new dynamic menu system
// import DynamicMenuRenderer from './DynamicMenuRenderer'

export function MenuInner() {
  const {formatMessage} = useIntl();
  const { supplierId } = useSupplierInfo(); // Get supplierId from context
  const navigate = useNavigate();

  // Check permissions for different modules
  const {canView: canViewDashboard} = usePermission('dashboard')
  const {canView: canViewProducts} = usePermission('products')
  // POs / Returns are Dumyah's purchase orders to a supplier; a partner SHOP sells off its own
  // shelf and is never sent one, so the whole menu (POs, Ordered / Removed Products, Delivery
  // Record, PO pick-up fees) is hidden from a shop account.
  const {currentUser} = SharedData()
  const {canView: canViewOrdersPermission} = usePermission('orders')
  const canViewOrders = canViewOrdersPermission && !isShopUser(currentUser)
  const unseenStockRequests = useUnseenStockRequests(canViewOrders)
  const dir = directionClasses(useLang())
  const {canView: canViewSalesReports} = usePermission('sales_reports')
  const {canView: canViewPaymentReports} = usePermission('payment_reports')
  const {canView: canViewDiscounts} = usePermission('discounts')
  // Daily Deals needs the user's RBAC permission AND the tenant feature (routes are gated by TenantFeatureRoute too).
  const {canView: canViewDailyDealsPermission} = usePermission('daily_deals')
  const canViewDailyDeals = canViewDailyDealsPermission && tenantFeatureEnabled('daily_deals')
  const {canView: canViewRequests} = usePermission('requests')
  const {canView: canViewUserManagement} = usePermission('user_management')
  function toLink(event: MouseEvent<HTMLAnchorElement>) {
    event.preventDefault();
    const target = event.currentTarget.href;
    navigateToLink(target);
  }
  function navigateToLink(target: string) {
    const url = new URL(target);
    const pathname = url.pathname + url.search; // remove the hostname and port
    navigate(pathname, { replace: false });
  }

    function getDateEnd() {
        const date = new Date();
        const formatted_Date = date.toLocaleDateString('en-US', {day: '2-digit' , month: '2-digit', year: 'numeric'});
        const [monthValue, dayValue, yearValue] = formatted_Date.split('/');
        return `${yearValue}-${monthValue}-${dayValue}`;
    }

    function getDateStart() {
        const date = new Date();
        date.setDate(date.getDate() - 7);
        const formatted_Date = date.toLocaleDateString('en-US', {day: '2-digit', month: '2-digit', year: 'numeric'});
        const [monthValue, dayValue, yearValue] = formatted_Date.split('/');
        return `${yearValue}-${monthValue}-${dayValue}`;
    }

    return (
    <>
      {canViewDashboard && (
        <MenuItem title={formatMessage({id: 'MENU.DASHBOARD'})} to='/dashboard' />
      )}
      {/* <MenuItem title='Layout Builder' to='/builder' /> */}

      {canViewProducts && (
        <MenuInnerWithSub
          title={formatMessage({id: 'prods'})}
          to='/products'
          menuPlacement='bottom-start'
          menuTrigger="{default:'click', lg: 'hover'}"
        >
          <MenuItem to='/products/product-add' title={formatMessage({id: 'add_single_prod'})} />
          {/* Removed the "Total Quantity in Warehouse" menu item */}
          <MenuItem to='/products/list' title={formatMessage({id: 'prods_list'})} />
          <MenuItem to='/offers/list' title={formatMessage({id: 'my_offers'})} />
          <MenuItem to='/products/brand/add' title={formatMessage({id: 'add_brand'})} />
          <MenuItem to='/products/bulk-add' title={formatMessage({id: 'add_using_bulk'})} />
          <MenuItem to='/products/bulk-update' title={formatMessage({id: 'update_using_bulk'})} />
          <MenuItem to='/products/drafted-list' title={formatMessage({id: 'draft_product'})} />
          <MenuItem
            to='/stock-report'
            title={formatMessage({id: 'MENU.PRODUCTS.STOCK_REPORT'})}
          />
        </MenuInnerWithSub>
      )}

      {canViewOrders && (
        <MenuInnerWithSub
            title={formatMessage({id: 'por_title_header'})}
            to=''
            menuPlacement='bottom-start'
            menuTrigger="{default:'click', lg: 'hover'}">
          <MenuItem title={formatMessage({id: 'por_title_header'})} to='/orders' />

              <MenuItem title={formatMessage({id: 'MENU.ORDERED_PRODUCTS'})} to='/ordered-products' />
              <MenuItem title={formatMessage({id: 'MENU.REMOVED_PRODUCTS'})} to='/orders/removed-products' />
              <MenuItem title={formatMessage({id: 'MENU.SUPPLIER_DELIVERY_RECORD'})} to='/orders/supplier-delivery-record' />
              <MenuItem
                title={formatMessage({id: 'MENU.ADD_TO_STOCK'})}
                to='/orders/add-to-stock'
                badge={
                  unseenStockRequests > 0 ? (
                    <span className={`badge badge-circle badge-danger ${dir.marginStart}`}>
                      {unseenStockRequests}
                    </span>
                  ) : undefined
                }
              />
          <MenuItem title={formatMessage({id: 'pickups_po_return_fees'})}
                     to={`/orders/pickups_po_return_fees?filter_date_start=${getDateStart()}&filter_date_end=${getDateEnd()}`} />
        </MenuInnerWithSub>
      )}
      {canViewDiscounts && (
        <MenuInnerWithSub
          title={formatMessage({id: 'add_discounts_label'})}
          to='/discount'
          menuPlacement='bottom-start'
          menuTrigger="{default:'click', lg: 'hover'}"
        >
          <MenuItem
              to='/discount/add-using-bulk'
              title={formatMessage({id: 'add_using_bulk'})}
          />
          <MenuItem
              to='/discount/add-selection'
              title={formatMessage({id: 'add_on_selected_prod'})}
          />
          <MenuItem
              to='/discount/remove-discount-bulk'
              title={formatMessage({id: 'disable_using_bulk'})}
          />
        </MenuInnerWithSub>
      )}
      {canViewPaymentReports && (
        <MenuInnerWithSub
          title={formatMessage({id: 'payment'})}
          to='/payment'
          menuPlacement='bottom-start'
          menuTrigger="{default:'click', lg: 'hover'}"
        >
          <MenuItem
              to={`/payment/transaction-report?filter_date_start=${getDateStart()}&filter_date_end=${getDateEnd()}`}
              title={formatMessage({id: 'transaction_report'})}
          />
          {/* Update link and title ID for the payment report */}
          <MenuItem
              to={`/payment/payment-report`}
              title={formatMessage({id: 'monthly_payment'})}
          />
        </MenuInnerWithSub>
      )}
      {canViewDailyDeals && (
        <MenuInnerWithSub
            title={formatMessage({id: 'daily_deals'})}
            to=''
            menuPlacement='bottom-start'
            menuTrigger="{default:'click', lg: 'hover'}">
          <MenuItem title={formatMessage({id: 'add_your_daily_deals'})} to='/daily-deal/list' />
          <MenuItem  title={formatMessage({id: 'daily_deals_products_list'})} to='/applied-daily-deal/list' />
        </MenuInnerWithSub>
      )}

      {canViewSalesReports && (
        <MenuInnerWithSub
            title={formatMessage({id: 'sales_title_header'})}
            to='/sales'
            menuPlacement='bottom-start'
            menuTrigger="{default:'click', lg: 'hover'}"
        >
          <MenuItem title={formatMessage({id: 'loss_of_opportunity'})} to='sales/loss-of-opportunity' />
          <MenuItem
              to={`/sales/sales-report?filter_date_start=${getDateStart()}&filter_date_end=${getDateEnd()}`}
              title={formatMessage({id: 'sales_report'})}
          />
          <MenuItem
              to={`/sales/in-transit?filter_date_start=${getDateStart()}&filter_date_end=${getDateEnd()}`}
              title={formatMessage({id: 'in_transit_report'})}
          />
        </MenuInnerWithSub>
      )}
      {canViewRequests && (
        <MenuItem title={formatMessage({id: 'requests'})} to='/request-add-products' />
      )}
      {canViewUserManagement && (
        <MenuItem title={formatMessage({id: 'user_management'})} to='/supplier-users' />
      )}
      <MenuItem title={formatMessage({id: 'FEEDBACK.SHARE'})} to='/feedback' />
      <MenuItem title={formatMessage({id: 'download_app'})} to='/download-app' variant='download' />

      {/* <MenuInnerWithSub title='Apps' to='/apps' menuPlacement='bottom-start' menuTrigger='click'>
        <MenuInnerWithSub
          title='Chat'
          to='/apps/chat'
          icon='/media/icons/duotune/communication/com012.svg'
          hasArrow={true}
          menuPlacement='right-start'
          menuTrigger={`{default:'click', lg: 'hover'}`}
        >
          <MenuItem to='/apps/chat/private-chat' title='Private Chat' hasBullet={true} />
          <MenuItem to='/apps/chat/group-chat' title='Group Chart' hasBullet={true} />
          <MenuItem to='/apps/chat/drawer-chat' title='Drawer Chart' hasBullet={true} />
        </MenuInnerWithSub>
        <MenuItem
          icon='/media/icons/duotune/general/gen051.svg'
          to='/apps/user-management/users'
          title='User management'
        />
      </MenuInnerWithSub> */}

      {/* <MenuInnerWithSub
        isMega={true}
        title='Mega menu'
        to='/mega-menu'
        menuPlacement='bottom-start'
        menuTrigger='click'
      >
        <MegaMenu />
      </MenuInnerWithSub> */}

      {/*
        ALTERNATIVE: Use the new dynamic menu system for cleaner code
        Replace the entire return statement above with:

        return (
          <DynamicMenuRenderer
            getDateStart={getDateStart}
            getDateEnd={getDateEnd}
          />
        )
      */}
    </>
  )
}
